// client/src/components/CatalogTab.jsx
// "קטלוג" tab on CampDetailPage. Edits the two camp-level catalogs:
//   - defaultEarnList  (good-deed items shown on EARN devices)
//   - defaultSpendList (sale items shown on SPEND devices)
//
// Both lists are stored as JSON arrays on the Camp row. The validator
// already accepts the shape [{ id?, label, amount }]; the service stamps
// missing ids server-side.
//
// UX:
//   - Drag-to-reorder via HTML5 DnD (no library — one-line index swap).
//   - Each item is a chunky card row: drag-handle · label · price-tag · ×.
//   - Live preview ("כך זה ייראה במסופון") under each section mirrors the
//     terminal grid — saves a context switch for the manager.
//   - Empty section gets a "starter pack" quick-add chip set so brand new
//     camps don't start staring at a blank page.
//   - Sticky dirty banner (clay) + bottom save button (btn-pop).
//   - Each section has Excel export (saved list) + import (parsed server-side,
//     dropped into the editor as replace/append — saved with the normal button).
//   - window.beforeunload fires when dirty.

import { useEffect, useRef, useState } from 'react';
import { useCamp, useUpdateCamp } from '../hooks/useCamps.js';
import Spinner from './Spinner.jsx';
import CatalogImportModal from './CatalogImportModal.jsx';
import {
  BanknotesIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
} from './Icons.jsx';
import { currencySymbol, formatNumber } from '../lib/currency.js';

// Pre-baked starter packs — tap once to seed an empty section. Hebrew copy
// targets the typical Israeli school / camp use-case so a manager can hit
// the ground running. Numbers are deliberately round.
const STARTER_EARN = [
  { label: 'תפילה יפה', amount: 10 },
  { label: 'עזרה לחבר', amount: 5 },
  { label: 'סדר בכיתה', amount: 5 },
  { label: 'שיעורי בית', amount: 8 },
  { label: 'התנדבות', amount: 15 },
];
const STARTER_SPEND = [
  { label: 'סוכריה', amount: 3 },
  { label: 'מדבקה', amount: 5 },
  { label: 'עפרון מיוחד', amount: 10 },
  { label: 'מחברת', amount: 15 },
  { label: 'משחק קטן', amount: 25 },
];

/** Programmatic file download — same effect as clicking an <a download>. */
function triggerDownload(url) {
  const a = document.createElement('a');
  a.href = url;
  a.download = '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function CatalogTab({ campId, currency, onDirtyChange }) {
  const campQ = useCamp(campId);
  const updateMutation = useUpdateCamp(campId);

  const [earnItems, setEarnItems] = useState([]);
  const [spendItems, setSpendItems] = useState([]);
  const originalRef = useRef({ earn: [], spend: [] });
  const [saveError, setSaveError] = useState(null);

  // Hydrate from the server. Re-syncs when the query refetches (e.g. after a
  // successful save — we re-fetch via mutation onSuccess).
  useEffect(() => {
    if (!campQ.data) return;
    const earn = normalize(campQ.data.defaultEarnList);
    const spend = normalize(campQ.data.defaultSpendList);
    setEarnItems(earn);
    setSpendItems(spend);
    originalRef.current = { earn, spend };
  }, [campQ.data]);

  const isDirty =
    !arraysEqual(earnItems, originalRef.current.earn) ||
    !arraysEqual(spendItems, originalRef.current.spend);

  // Browser-native unload guard while dirty.
  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Bubble dirty state up so CampDetailPage can guard tab switches.
  useEffect(() => { if (onDirtyChange) onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);
  useEffect(() => () => { if (onDirtyChange) onDirtyChange(false); }, [onDirtyChange]);

  async function handleSave() {
    setSaveError(null);
    try {
      const cleanedEarn  = earnItems.filter((i) => i.label.trim().length > 0);
      const cleanedSpend = spendItems.filter((i) => i.label.trim().length > 0);
      await updateMutation.mutateAsync({
        defaultEarnList: cleanedEarn,
        defaultSpendList: cleanedSpend,
      });
      originalRef.current = { earn: cleanedEarn, spend: cleanedSpend };
      setEarnItems(cleanedEarn);
      setSpendItems(cleanedSpend);
    } catch (err) {
      setSaveError(err.message || 'שגיאה בשמירה');
    }
  }

  if (campQ.isLoading) return <Spinner label="טוען קטלוג..." />;
  if (campQ.isError) {
    return (
      <div className="card-danger p-4 text-joy-900 text-sm">
        שגיאה: {campQ.error.message}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {isDirty && (
        <div className="sticky top-[64px] z-20 rounded-2xl bg-clay-50 border border-clay-200 text-clay-900 px-4 py-2.5 flex items-center justify-between gap-3 shadow-card animate-slide-up">
          <span className="text-sm font-semibold inline-flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4" />
            יש שינויים שלא נשמרו
          </span>
          <button
            type="button"
            onClick={handleSave}
            className="btn-primary btn-sm"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'שומר...' : 'שמור שינויים'}
          </button>
        </div>
      )}

      {saveError && (
        <div className="card-danger p-3 text-joy-800 text-sm">{saveError}</div>
      )}

      <CatalogSection
        Icon={BanknotesIcon}
        title="פעולות לזיכוי"
        subtitle="מורים יבחרו מהרשימה הזו כשהם מזכים תלמיד נקודות"
        campId={campId}
        listKey="earn"
        items={earnItems}
        onChange={setEarnItems}
        starterPack={STARTER_EARN}
        currency={currency}
        tone="mint"
      />

      <CatalogSection
        Icon={ShoppingBagIcon}
        title="מוצרים למכירה"
        subtitle="עמדות מכירה (קיוסק / דוכן) יציגו את הרשימה הזו לתלמידים"
        campId={campId}
        listKey="spend"
        items={spendItems}
        onChange={setSpendItems}
        starterPack={STARTER_SPEND}
        currency={currency}
        tone="sky"
      />

      <div className="flex justify-end pt-1">
        <button
          type="button"
          onClick={handleSave}
          className="btn-pop btn-lg"
          disabled={!isDirty || updateMutation.isPending}
        >
          {updateMutation.isPending ? 'שומר...' : 'שמור שינויים'}
        </button>
      </div>
    </div>
  );
}

const TONE = {
  mint: {
    strap: 'strap-earn',
    tile:  'bg-mint-100 text-mint-700',
    badge: 'badge-mint',
    label: 'EARN',
    preview: 'border-mint-300 bg-mint-50/40 text-mint-800',
  },
  sky: {
    strap: 'strap-spend',
    tile:  'bg-sky-100 text-sky-700',
    badge: 'badge-sky',
    label: 'SPEND',
    preview: 'border-sky-300 bg-sky-50/40 text-sky-800',
  },
};

function CatalogSection({ Icon, title, subtitle, campId, listKey, items, onChange, starterPack, currency, tone }) {
  const t = TONE[tone];
  const symbol = currencySymbol(currency);
  // SHEKEL/DOLLAR camps may price with agorot (2 dp); STAR stays whole.
  // The server enforces this too (assertCurrencyAmounts) — this is UX only.
  const isStar = currency === 'STAR';
  const [importOpen, setImportOpen] = useState(false);

  // Export reflects the SAVED list — unsaved editor rows aren't on the server
  // yet, so nudge the manager to save first.
  const savedCount = items.filter((i) => i.id).length;
  function handleExport() {
    if (items.some((i) => !i.id) && !window.confirm('יש פריטים שעדיין לא נשמרו — הם לא ייכללו בקובץ. להוריד בכל זאת?')) return;
    triggerDownload(`/api/camps/${campId}/catalog/${listKey}/export.xlsx`);
  }
  function applyImport(imported, mode) {
    onChange(mode === 'append' ? [...items, ...imported] : imported);
  }

  function updateItem(idx, field, value) {
    const next = [...items];
    next[idx] = { ...next[idx], [field]: value };
    onChange(next);
  }
  function removeItem(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }
  function addItem() {
    onChange([...items, { label: '', amount: 5 }]);
  }
  function moveItemTo(fromIdx, toIdx) {
    if (fromIdx === toIdx) return;
    const next = [...items];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    onChange(next);
  }
  function applyStarter() {
    onChange(starterPack.map((s) => ({ ...s })));
  }

  // Preview the catalog as it would render on the terminal — only items
  // with a non-empty label survive (matches what we'd save).
  const previewItems = items.filter((i) => i.label.trim().length > 0);

  return (
    <div className="card overflow-hidden">
      <div className={t.strap} />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-4">
          <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${t.tile}`}>
            {Icon && <Icon className="w-5 h-5" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="h-section">{title}</h2>
              <span className={t.badge}>{t.label}</span>
              {items.length > 0 && (
                <span className="badge-slate num">{items.length}</span>
              )}
            </div>
            <p className="text-sm text-ink-600 mt-1">{subtitle}</p>
          </div>
          <div className="flex gap-1.5 shrink-0">
            <button
              type="button"
              onClick={handleExport}
              className="btn-secondary btn-sm"
              title={savedCount === 0 ? 'מוריד קובץ תבנית ריק' : 'ייצוא הרשימה השמורה לאקסל'}
            >
              ייצוא
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="btn-secondary btn-sm"
              title="ייבוא פריטים מקובץ אקסל"
            >
              ייבוא
            </button>
          </div>
        </div>

        <CatalogImportModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          campId={campId}
          list={listKey}
          title={title}
          symbol={symbol}
          isStar={isStar}
          currentCount={items.length}
          onApply={applyImport}
        />

        {items.length === 0 ? (
          <EmptyHint
            tone={tone}
            starter={starterPack}
            symbol={symbol}
            onApply={applyStarter}
            onCustom={addItem}
          />
        ) : (
          <DraggableList
            items={items}
            symbol={symbol}
            isStar={isStar}
            onUpdate={updateItem}
            onRemove={removeItem}
            onMove={moveItemTo}
            tone={tone}
          />
        )}

        {items.length > 0 && (
          <button type="button" onClick={addItem} className="btn-secondary btn-sm mt-3">
            + הוסף פריט
          </button>
        )}

        {/* Live preview — mirrors the terminal catalog grid so the manager
            can see exactly how their list will look to teachers / students
            before they save. Only renders when there's at least one item
            with a label, otherwise an empty preview is just noise. */}
        {previewItems.length > 0 && <TerminalPreview items={previewItems} symbol={symbol} tone={t} />}
      </div>
    </div>
  );
}

/* ───── Empty state — starter pack quick-add ─────────────────── */

function EmptyHint({ tone, starter, symbol, onApply, onCustom }) {
  return (
    <div className="rounded-2xl border-2 border-dashed border-ink-200 p-5 sm:p-6 text-center">
      <p className="text-sm text-ink-600 mb-3">
        הרשימה ריקה. אפשר להתחיל מחבילה מומלצת או להוסיף ידני.
      </p>
      <div className="flex flex-wrap gap-1.5 justify-center mb-4">
        {starter.map((s) => (
          <span key={s.label} className="chip">
            <span>{s.label}</span>
            <span className="text-ink-400">·</span>
            <span className="num font-bold text-ink-700">{symbol}{s.amount}</span>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-2 justify-center">
        <button
          type="button"
          onClick={onApply}
          className={`btn ${tone === 'mint' ? 'bg-mint-600 hover:bg-mint-700' : 'bg-sky-600 hover:bg-sky-700'} text-white shadow-sm`}
        >
          ✨ השתמש בחבילה
        </button>
        <button type="button" onClick={onCustom} className="btn-secondary">
          + פריט מותאם
        </button>
      </div>
    </div>
  );
}

/* ───── Draggable list ───────────────────────────────────────────────
   Pointer-event based reorder — no HTML5 DnD, no browser ghost image.
   Press the drag handle, drag, drop. The dragged row follows the
   cursor smoothly via transform: translateY; the row currently under
   the cursor is detected via document.elementFromPoint and the other
   rows transition out of the way to make room. Touch + mouse + pen all
   route through pointer events uniformly.
   ──────────────────────────────────────────────────────────────────── */

const GAP_PX = 8; // matches space-y-2 between rows

function DraggableList({ items, symbol, isStar, onUpdate, onRemove, onMove, tone }) {
  // drag = null when idle; otherwise:
  //   { fromIdx, hoverIdx, deltaY, startY, dragH, pointerId }
  const [drag, setDrag] = useState(null);
  const rowRefs = useRef({});

  function startDrag(idx, e) {
    if (e.button !== undefined && e.button !== 0) return; // only primary
    // Don't start a drag if the user clicked to text-select the label
    // input or to type into the amount — drag handle only.
    const dragH = rowRefs.current[idx]?.offsetHeight ?? 56;
    setDrag({
      fromIdx: idx,
      hoverIdx: idx,
      startY: e.clientY,
      deltaY: 0,
      dragH,
      pointerId: e.pointerId,
    });
    try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch {/* ignore */}
    e.preventDefault();
  }

  // Window-level move / up so the gesture stays alive when the pointer
  // leaves the handle. setPointerCapture above forwards to the captured
  // element instead of the window for browsers that support it, but the
  // window fallback is harmless.
  useEffect(() => {
    if (!drag) return undefined;

    function handleMove(ev) {
      const clientX = ev.clientX;
      const clientY = ev.clientY;
      const deltaY = clientY - drag.startY;

      // Find which row sits under the cursor by hiding the dragged row
      // briefly and asking the browser. Cleaner than computing geometry
      // for variable-height rows.
      const dragged = rowRefs.current[drag.fromIdx];
      if (!dragged) return;
      const prevPE = dragged.style.pointerEvents;
      dragged.style.pointerEvents = 'none';
      const under = document.elementFromPoint(clientX, clientY);
      dragged.style.pointerEvents = prevPE;

      let row = under;
      while (row && !(row.dataset && row.dataset.rowIdx != null)) row = row.parentElement;
      const hoverIdx = row != null ? parseInt(row.dataset.rowIdx, 10) : drag.hoverIdx;

      setDrag((d) => (d ? { ...d, deltaY, hoverIdx } : d));
      ev.preventDefault();
    }

    function handleUp() {
      setDrag((d) => {
        if (!d) return d;
        if (d.hoverIdx !== d.fromIdx) onMove(d.fromIdx, d.hoverIdx);
        return null;
      });
    }

    window.addEventListener('pointermove', handleMove, { passive: false });
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
    // onMove identity is stable enough — DraggableList is short-lived per
    // section and onMove is created fresh on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.fromIdx, drag?.startY, drag?.hoverIdx, drag?.dragH]);

  // For each non-dragged row, figure out how much to slide so the gap
  // appears at hoverIdx. shift = ±(dragH + GAP_PX). Dragged row itself
  // gets transform: translateY(deltaY) to follow the cursor.
  function transformFor(idx) {
    if (!drag) return '';
    const { fromIdx, hoverIdx, deltaY, dragH } = drag;
    if (idx === fromIdx) {
      return `translateY(${deltaY}px) scale(1.02) rotate(-0.4deg)`;
    }
    if (hoverIdx > fromIdx && idx > fromIdx && idx <= hoverIdx) {
      return `translateY(${-(dragH + GAP_PX)}px)`;
    }
    if (hoverIdx < fromIdx && idx >= hoverIdx && idx < fromIdx) {
      return `translateY(${dragH + GAP_PX}px)`;
    }
    return '';
  }

  return (
    <ul className="space-y-2 select-none">
      {items.map((item, idx) => {
        const isDragging = drag?.fromIdx === idx;
        const transform = transformFor(idx);
        return (
          <li
            key={item.id || `new-${idx}`}
            ref={(el) => { rowRefs.current[idx] = el; }}
            data-row-idx={idx}
            style={{
              transform,
              // The DRAGGED row should snap to the pointer with no delay;
              // OTHER rows transition smoothly while making room. The
              // distinction is: skip transition only on the dragged row.
              transition: isDragging
                ? 'box-shadow 120ms ease'
                : 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1)',
              zIndex: isDragging ? 30 : 1,
              position: 'relative',
              boxShadow: isDragging
                ? '0 18px 36px -16px rgba(15,23,42,0.32), 0 6px 12px -6px rgba(15,23,42,0.18)'
                : undefined,
            }}
            className={`flex gap-2 items-stretch rounded-2xl border bg-white
              ${isDragging
                ? 'border-brand-500 ring-2 ring-brand-300'
                : 'border-ink-200 hover:border-brand-300 hover:shadow-card transition-colors'}`}
          >
            <DragHandle
              onPointerDown={(e) => startDrag(idx, e)}
              active={isDragging}
            />
            <input
              type="text"
              className="flex-1 bg-transparent px-2 py-3 text-sm font-medium text-ink-900 focus:outline-none"
              aria-label="תיאור הפריט"
              value={item.label}
              onChange={(e) => onUpdate(idx, 'label', e.target.value)}
              maxLength={50}
              disabled={isDragging}
            />
            <PriceTag
              symbol={symbol}
              value={item.amount ?? ''}
              tone={tone}
              isStar={isStar}
              onChange={(v) =>
                onUpdate(
                  idx,
                  'amount',
                  v === ''
                    ? ''
                    : isStar
                      ? parseInt(v, 10) || 0
                      : Math.round(parseFloat(v) * 100) / 100 || 0
                )
              }
              disabled={isDragging}
            />
            <button
              type="button"
              onClick={() => onRemove(idx)}
              className="text-joy-600 hover:bg-joy-50 w-10 grid place-items-center text-xl rounded-l-2xl transition-colors shrink-0"
              aria-label="מחק פריט"
            >×</button>
          </li>
        );
      })}
    </ul>
  );
}

function DragHandle({ onPointerDown, active }) {
  return (
    <div
      onPointerDown={onPointerDown}
      role="button"
      tabIndex={-1}
      aria-label="גרור לסידור מחדש"
      title="גרור לסידור מחדש"
      className={`w-9 grid place-items-center rounded-r-2xl shrink-0 select-none
        touch-none transition-colors
        ${active
          ? 'bg-brand-100 text-brand-700 cursor-grabbing'
          : 'bg-ink-50/60 text-ink-400 hover:bg-ink-100 hover:text-ink-700 cursor-grab active:cursor-grabbing'}`}
      style={{ touchAction: 'none' }}
    >
      <svg className="w-4 h-4" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
        <circle cx="5"  cy="3"  r="1.4" /><circle cx="11" cy="3"  r="1.4" />
        <circle cx="5"  cy="8"  r="1.4" /><circle cx="11" cy="8"  r="1.4" />
        <circle cx="5"  cy="13" r="1.4" /><circle cx="11" cy="13" r="1.4" />
      </svg>
    </div>
  );
}

function PriceTag({ symbol, value, onChange, tone, disabled, isStar }) {
  // Tone-tinted price box so the visual mode (EARN/SPEND) reads in the
  // editor row too — not just on the section header.
  const cls =
    tone === 'mint'
      ? 'bg-mint-50 border-mint-200 text-mint-800'
      : 'bg-sky-50 border-sky-200 text-sky-800';
  return (
    <div className={`flex items-center gap-0.5 px-2 rounded-xl border ${cls} shrink-0 my-1.5`}>
      <span className="text-base font-bold opacity-70">{symbol}</span>
      <input
        type="number"
        className="w-20 text-center bg-transparent px-1 py-1 text-base font-extrabold num focus:outline-none"
        aria-label="נקודות"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        min={isStar ? 1 : 0.01}
        max={9999}
        step={isStar ? 1 : 0.01}
        inputMode={isStar ? 'numeric' : 'decimal'}
        disabled={disabled}
      />
    </div>
  );
}

/* ───── Terminal preview ─────────────────────────────────────── */

function TerminalPreview({ items, symbol, tone }) {
  return (
    <div className="mt-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-bold uppercase tracking-wider text-ink-500">
          כך זה ייראה במסופון
        </p>
        <span className="badge-slate">{items.length} אריחים</span>
      </div>
      <div className={`rounded-2xl border-2 border-dashed ${tone.preview} p-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2`}>
        {items.map((it, idx) => (
          <div
            key={it.id || idx}
            className="rounded-xl border-2 border-white bg-white shadow-card px-2 py-2.5 text-center min-h-[64px] flex flex-col justify-center"
          >
            <div className="text-xs font-semibold text-ink-800 clamp-2 leading-tight">
              {it.label}
            </div>
            <div className="mt-1 text-sm font-extrabold num text-brand-700">
              {symbol}{formatNumber(it.amount || 0)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ───── helpers ──────────────────────────────────────────────── */

function normalize(list) {
  if (!Array.isArray(list)) return [];
  return list.map((item) => ({
    id: item.id,
    label: item.label || '',
    amount: typeof item.amount === 'number' ? item.amount : 0,
  }));
}

function arraysEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].label !== b[i].label) return false;
    if (a[i].amount !== b[i].amount) return false;
    if (a[i].id !== b[i].id) return false;
  }
  return true;
}
